import DineroFactory from "dinero.js";
import { has, isMatch, pick } from "lodash";
import { type CurrencyCode, type CurrencyObjectType, type Money } from "common/types/mainTypes";
import { type WalletMonies } from "common/components/Wallet/types";
import { type CurrencyCodeType, getCurrencyName } from "common/data/graphql/Enums/currency";
import { type EditMoneyType } from "common/forms/fields/types";
import { DEFAULT_BASE_MONEY_PROPS } from "common/components/Money/constants";
import { MoneyFormat } from "./index";

/**
 * Determines if a MoneyObject in the shape of a MoneyFragment has a nonzero amount.
 */
export const isNonZero = (money: Pick<Money, "amount"> | null | undefined): boolean =>
  Boolean(money?.amount);

export const isCurrency = (value: unknown): value is Money => has(value, "currency");

export const isWalletMonies = (value: unknown): value is WalletMonies => has(value, "monies");

/**
 * Returns the currency symbol based on the browser's locale.
 * This is needed as symbols may change based on locale.
 * Example is Norwegian Kron which has a symbol of 'NOK' for 'en-US' locale
 * and 'kr' for 'no-NO' locale. Will also account for scenarios such
 * as Canadian users who will see CAD represented as '$' instead of 'CA$'.
 * @param currency - Currency code to get the symbol
 */
export const getCurrencySymbol = (currency: CurrencyCode) => {
  const locale = window.navigator.language || "en-US";
  return (0)
    .toLocaleString(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace(/\d/g, "")
    .trim();
};

/**
 * Determines if two MoneyObjects in the shape of MoneyFragments are equal.
 */
export const areEqual = (money1: Money | null, money2: Money | null): boolean => {
  if (!money1 || !money2) {
    return false;
  }
  return isMatch(money1, pick(money2, ["amount", "currency", "precision"]));
};

/**
 * Determines if a list of MoneyObjects in the shape of MoneyFragments has at least one nonzero amount.
 * @param currencyValues list of MoneyObjects
 */
export const hasNonZeroValue = (currencyValues: null | Money[] | WalletMonies) => {
  if (currencyValues == null) {
    return false;
  }
  if (isWalletMonies(currencyValues)) {
    return currencyValues.monies.some(isNonZero);
  }
  return currencyValues.some(isNonZero);
};

const formatNumber = (units: number): string => {
  if (units >= 1e12) {
    const inTrillions = units / 1e12;
    return `${inTrillions.toPrecision(3)}T`;
  }
  if (units >= 1e9) {
    const inBillions = units / 1e9;
    return `${inBillions.toPrecision(3)}B`;
  }
  if (units >= 1e6) {
    const inMillions = units / 1e6;
    return `${inMillions.toPrecision(3)}M`;
  }
  return Math.round(units).toLocaleString();
};

const formatNumberForNPHomepage = (amount: number) => {
  if (amount >= 1e12) {
    const inTrillions = amount / 1e12;
    return `${inTrillions.toFixed(1)} trillion`;
  }
  if (amount >= 1e9) {
    const inBillions = amount / 1e9;
    return `${inBillions.toFixed(1)} billion`;
  }
  if (amount >= 1e6) {
    const inMillions = amount / 1e6;
    return `${inMillions.toFixed(1)} million`;
  }
  if (amount >= 1000) {
    const inThousands = amount / 1000;
    return `${inThousands.toFixed(1)} thousand`;
  }
  return "";
};

export const summaryFormat = (
  amount: number,
  currencyCode: CurrencyCodeType,
  hideSymbol = false,
): string => {
  let result = "";
  if (!hideSymbol) {
    result += getCurrencySymbol(currencyCode) || "";
  }
  result += formatNumber(amount);
  return result;
};

export function getFullNameAndCodeForCurrency(currency: CurrencyObjectType): string {
  return `${currency.fullName} (${currency.code})`;
}

export const getTotalMonies = (monies: Money[] | null | undefined) => {
  if (!monies) {
    return null;
  }
  return monies.reduce(
    (totalMonies, { amount, currency, precision }) => ({
      amount: totalMonies.amount + amount,
      currency,
      precision,
    }),
    { ...DEFAULT_BASE_MONEY_PROPS },
  );
};

/**
 * Looks through an array of money and returns whether there are more than one currency in the
 * array.
 *
 * @param monies The array of money to go through.
 * @returns true if there is more than one currency in the array.
 */
export const hasMultipleCurrencies = (monies: Money[] | null | undefined) => {
  if (!monies) {
    return false;
  }

  const currencies = new Set(monies.map((money) => money.currency));
  return currencies.size > 1;
};

export const getMultipleCurrenciesTooltip = (currency: CurrencyCodeType) =>
  `Some investments have values that have been converted from their original currencies to ${getCurrencyName(
    currency,
  )} (${getCurrencySymbol(currency)}).`;

export const getTargetCurrencyWalletTooltip = (
  originalCurrencies: CurrencyCode[],
  currency: CurrencyCode,
) => {
  const uniqueCurrencies = Array.from(new Set(originalCurrencies));
  const originalCurrencyNames = uniqueCurrencies.map(
    (c) => `${getCurrencyName(c)} (${getCurrencySymbol(c)})`,
  );
  return `This value has been converted from ${originalCurrencyNames.join(
    ", ",
  )} to ${getCurrencyName(currency)} (${getCurrencySymbol(
    currency,
  )}). You can click into the investment to view data in the local currency.`;
};
/**
 * CurrencyField uses EditMoneyType which is similar to Money but allows
 * amount to be a string or a number. When the input has been parsed to a valid money
 * value, amount will be a number. This function checks the type of amount,
 * which can be used, for example, to check that it's ok to run schema-level
 * validations that expect a valid money value.
 */
export const hasNumberValue = (editVal: EditMoneyType): editVal is Money =>
  typeof editVal.amount === "number";

export const transformMoneyTypes = (moneyObjectTypes: Money[] | null): EditMoneyType[] =>
  moneyObjectTypes?.map((m) => pick(m, "amount", "currency", "precision")) || [];

export const toMoney = (editMoneyType: EditMoneyType): Money | null => {
  if (!hasNumberValue(editMoneyType)) {
    return null;
  }
  return editMoneyType;
};

export const formatMoney = (
  money: Money,
  moneyFormat: MoneyFormat,
  rounded = true,
  precision: undefined | number = undefined,
) => {
  /**
   * @param rounded   Round to the nearest whole dollar using half away from zero rule.
   */
  const { currency, amount } = money;
  const dinero = DineroFactory({
    currency: money.currency,
    amount: Number(amount),
    precision: money.precision,
  });
  const unitsAbsolute = Math.abs(rounded ? dinero.toRoundedUnit(0) : dinero.toUnit());
  const userLocale = window.navigator.language;

  const options: Intl.NumberFormatOptions = {
    style: "currency",
    minimumFractionDigits: rounded ? 0 : precision, // (minimum|maximum)FractionDigits is required for rounded values
    maximumFractionDigits: rounded ? 0 : precision, // they must have the same value; otherwise a RangeError is thrown
    currency,
  };

  let format;
  switch (moneyFormat) {
    case MoneyFormat.Summary:
      format = `${getCurrencySymbol(currency)} ${formatNumberForNPHomepage(unitsAbsolute)}`;
      break;

    case MoneyFormat.SummaryWithCode:
      format = `${currency} ${formatNumber(unitsAbsolute)}`;
      break;

    case MoneyFormat.SummaryWithSymbol:
      format = `${getCurrencySymbol(currency)}${formatNumber(unitsAbsolute)}`;
      break;

    case MoneyFormat.SymbolAndCode:
      format = `${unitsAbsolute.toLocaleString(userLocale, options)} ${currency}`;
      break;

    case MoneyFormat.Amount:
      format = Math.abs(dinero.toUnit()).toLocaleString(userLocale); // TODO rounding ignored here? is that accurate?
      break;

    case MoneyFormat.Symbol:
    default:
      format = unitsAbsolute.toLocaleString(userLocale, options);
  }

  return dinero.isNegative() ? `(${format})` : format;
};
